/**
 * Anmeldung und „Passwort vergessen“. Beide laufen über Cognito; für noch nicht
 * übernommene Konten greift dabei der Migrationstrigger.
 */
import { Alert, Anchor, Button, Center, Paper, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { AuthError, type AuthErrorCode } from '../auth/cognito.js';
import { t } from '../texts.js';
import { useServices } from '../services.js';
import { BackButton } from './back-link.js';

/** E-Mail vom Login an „Passwort vergessen“ übergeben, ohne sie in die URL zu schreiben. */
let emailHandoff = '';
/** Gesetzt, wenn Cognito ein neues Passwort verlangt (Altpasswort unter 8 Zeichen). */
let resetRequiredHandoff = false;

function errorCode(error: unknown): AuthErrorCode {
  return error instanceof AuthError ? error.code : 'unknown';
}

function AuthFrame({ title, onBack, children }: { title: string; onBack?: () => void; children: ReactNode }) {
  return (
    <Center mih="100vh" p="md" bg="var(--mantine-color-gray-0)">
      <Paper withBorder shadow="sm" p="xl" radius="md" w="100%" maw={420}>
        <Stack gap="md">
          {onBack && <BackButton onClick={onBack} />}
          <Text c="dimmed" size="sm" fw={600}>
            {t('app.name')}
          </Text>
          <Title order={2}>{title}</Title>
          {children}
        </Stack>
      </Paper>
    </Center>
  );
}

export function LoginPage({ redirect }: { redirect?: string | undefined }) {
  const { auth, authMode } = useServices();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<AuthErrorCode | undefined>();
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await auth.signIn(email, password);
      // Nur interne Pfade als Rücksprungziel zulassen.
      const target = redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/';
      await navigate({ to: target });
    } catch (caught) {
      const code = errorCode(caught);
      if (code === 'reset-required') {
        // Altpasswort zu kurz: direkt in „neues Passwort setzen“, Code wird dort angefordert.
        emailHandoff = email;
        resetRequiredHandoff = true;
        await navigate({ to: '/forgot' });
        return;
      }
      setError(code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame title={t('login.title')}>
      <Text size="sm">{authMode === 'dev' ? t('login.devHint') : t('login.hint')}</Text>
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <TextInput
            label={t('login.email')}
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={e => setEmail(e.currentTarget.value)}
          />
          <PasswordInput
            label={t('login.password')}
            autoComplete="current-password"
            required={authMode !== 'dev'}
            value={password}
            onChange={e => setPassword(e.currentTarget.value)}
          />
          {error && (
            <Alert color="red" role="alert">
              {t(`auth.error.${error}`)}
              {error === 'invalid-credentials' && (
                <Text size="xs" mt={4}>
                  {t('login.resetPendingHint')}
                </Text>
              )}
            </Alert>
          )}
          <Button type="submit" loading={busy} disabled={authMode !== 'dev' && (!email || !password)}>
            {t('login.submit')}
          </Button>
          <Anchor
            component="button"
            type="button"
            size="sm"
            onClick={() => {
              emailHandoff = email;
              void navigate({ to: '/forgot' });
            }}
          >
            {t('login.forgot')}
          </Anchor>
        </Stack>
      </form>
    </AuthFrame>
  );
}

export function ForgotPasswordPage() {
  const { auth } = useServices();
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'confirm' | 'done'>('request');
  // Übergabe im Initialisierer nur lesen und erst im Effekt löschen (StrictMode ruft doppelt).
  const [email, setEmail] = useState(() => emailHandoff);
  const [resetRequired] = useState(() => resetRequiredHandoff);
  useEffect(() => {
    emailHandoff = '';
    resetRequiredHandoff = false;
  }, []);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<AuthErrorCode | undefined>();
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>, next: typeof step) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      setStep(next);
    } catch (caught) {
      const code = errorCode(caught);
      // Keine Auskunft, ob ein Konto existiert.
      if (step === 'request' && code === 'invalid-credentials') setStep(next);
      else setError(code);
    } finally {
      setBusy(false);
    }
  }

  // Bei verlangtem Neusetzen den Code gleich anfordern (einmalig, auch unter StrictMode).
  const requested = useRef(false);
  useEffect(() => {
    if (resetRequired && email && !requested.current) {
      requested.current = true;
      void run(() => auth.forgotPassword(email), 'confirm');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim ersten Anzeigen
  }, []);

  return (
    <AuthFrame title={t('forgot.title')} onBack={() => void navigate({ to: '/login' })}>
      {resetRequired && step !== 'done' && <Alert color="yellow">{t('forgot.resetRequired')}</Alert>}
      {step === 'request' && (
        <form
          onSubmit={e => {
            e.preventDefault();
            void run(() => auth.forgotPassword(email), 'confirm');
          }}
        >
          <Stack gap="sm">
            <TextInput label={t('login.email')} type="email" autoComplete="username" required value={email} onChange={e => setEmail(e.currentTarget.value)} />
            <Button type="submit" loading={busy} disabled={!email}>
              {t('forgot.request')}
            </Button>
          </Stack>
        </form>
      )}
      {step === 'confirm' && (
        <form
          onSubmit={e => {
            e.preventDefault();
            void run(() => auth.confirmForgotPassword(email, code, newPassword), 'done');
          }}
        >
          <Stack gap="sm">
            <Alert color="blue">{t('forgot.sent')}</Alert>
            <TextInput label={t('forgot.code')} autoComplete="one-time-code" required value={code} onChange={e => setCode(e.currentTarget.value)} />
            <PasswordInput
              label={t('forgot.newPassword')}
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={e => setNewPassword(e.currentTarget.value)}
            />
            <Button type="submit" loading={busy} disabled={!code || !newPassword}>
              {t('forgot.confirm')}
            </Button>
          </Stack>
        </form>
      )}
      {step === 'done' && <Alert color="green">{t('forgot.done')}</Alert>}
      {error && (
        <Alert color="red" role="alert">
          {t(`auth.error.${error}`)}
        </Alert>
      )}
    </AuthFrame>
  );
}
